import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getLaporanKinerja, getSekolahList, getTimezoneSettings, getLaporanIndividu, getKinerjaAggregate } from '../../lib/api';
import { getMonthName, getScoreColor, getScoreBadge } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, FileText, Loader2, 
  TrendingUp, Award, Users, FileSpreadsheet, Building2, UserCircle, Eye, Search, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from '../ui/sonner';
import html2canvas from 'html2canvas';

const ITEMS_PER_PAGE = 15;

const LaporanPage = () => {
  const { user, isAdminDinas } = useAuth();
  const navigate = useNavigate();
  const [laporan, setLaporan] = useState(null);
  const [kinerjaAggregate, setKinerjaAggregate] = useState(null);
  const [sekolahList, setSekolahList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedSekolah, setSelectedSekolah] = useState('all');
  const [timezoneSettings, setTimezoneSettings] = useState({ timezone: 'WIB' });
  const [exporting, setExporting] = useState(false);
  const [exportingIndividual, setExportingIndividual] = useState(null);
  const chartRef = useRef(null);
  
  // Pagination & Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef(null);

  // Debounce search
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1); // Reset to first page on search
    }, 400);
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear, selectedSekolah, currentPage, debouncedSearch]);

  useEffect(() => {
    // Load timezone settings and sekolah list once
    getTimezoneSettings().then(setTimezoneSettings).catch(console.error);
    if (isAdminDinas) {
      getSekolahList().then(setSekolahList).catch(console.error);
    }
  }, [isAdminDinas]);

  const loadData = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      const sekolahId = selectedSekolah === 'all' ? null : selectedSekolah;
      
      // Fetch paginated data and aggregate in parallel
      const [laporanData, aggregateData] = await Promise.all([
        getLaporanKinerja(selectedMonth, selectedYear, sekolahId, ITEMS_PER_PAGE, offset, debouncedSearch),
        getKinerjaAggregate(selectedMonth, selectedYear, sekolahId)
      ]);
      
      setLaporan(laporanData);
      setKinerjaAggregate(aggregateData);
    } catch (error) {
      console.error('Failed to load laporan:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chart data from aggregate - Top performers
  const chartData = kinerjaAggregate?.top_performers?.map(g => ({
    name: g.guru_nama.length > 12 ? g.guru_nama.substring(0, 12) + '...' : g.guru_nama,
    skor: g.skor_kinerja,
    hadir: g.hadir_tepat_waktu + g.hadir_terlambat
  })) || [];

  // Pie chart data for grade distribution
  const pieDataGrades = kinerjaAggregate?.distribusi?.map(d => ({
    name: `${d.grade} - ${d.label}`,
    value: d.count,
    color: d.color
  })).filter(d => d.value > 0) || [];

  // Pagination calculations
  const totalItems = laporan?.total || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Get selected school name
  const getSelectedSekolahName = () => {
    if (selectedSekolah === 'all') return 'Semua Sekolah';
    const school = sekolahList.find(s => s.id === selectedSekolah);
    return school?.nama || 'Sekolah';
  };

  // Export to PDF - Enhanced version
  const exportPDF = async () => {
    setExporting(true);
    try {
      // Fetch ALL data without pagination for export
      const sekolahId = selectedSekolah === 'all' ? null : selectedSekolah;
      const allData = await getLaporanKinerja(selectedMonth, selectedYear, sekolahId, null, 0, null);
      const exportData = allData?.data || [];
      
      if (exportData.length === 0) {
        toast.error('Tidak ada data untuk diexport');
        return;
      }
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header - Institution Info
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('PEMERINTAH KABUPATEN ASMAT', pageWidth / 2, 15, { align: 'center' });
      doc.setFontSize(12);
      doc.text('DINAS PENDIDIKAN', pageWidth / 2, 22, { align: 'center' });
      
      // Line separator
      doc.setDrawColor(30, 27, 75);
      doc.setLineWidth(0.5);
      doc.line(20, 27, pageWidth - 20, 27);
      doc.setLineWidth(0.2);
      doc.line(20, 28, pageWidth - 20, 28);
      
      // Report Title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('LAPORAN KINERJA PEGAWAI', pageWidth / 2, 38, { align: 'center' });
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Periode: ${getMonthName(selectedMonth)} ${selectedYear}`, pageWidth / 2, 45, { align: 'center' });
      doc.text(`Sekolah: ${getSelectedSekolahName()}`, pageWidth / 2, 51, { align: 'center' });
      
      // Summary Statistics - using exportData (all data) instead of laporan.data (paginated)
      const pdfAvgScore = exportData.length > 0 
        ? (exportData.reduce((sum, g) => sum + g.skor_kinerja, 0) / exportData.length).toFixed(1)
        : 0;
      const pdfTotalGuru = exportData.length || 0;
      const totalTepat = exportData.reduce((sum, g) => sum + g.hadir_tepat_waktu, 0) || 0;
      const totalTerlambat = exportData.reduce((sum, g) => sum + g.hadir_terlambat, 0) || 0;
      const totalMangkir = exportData.reduce((sum, g) => sum + g.mangkir, 0) || 0;
      const totalIzin = exportData.reduce((sum, g) => sum + g.izin, 0) || 0;
      
      // Summary Box
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(20, 55, pageWidth - 40, 25, 3, 3, 'F');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Pegawai: ${pdfTotalGuru}`, 30, 63);
      doc.text(`Rata-rata Skor: ${pdfAvgScore}`, 70, 63);
      doc.text(`Hari Kerja: ${allData?.total_hari_kerja || 0}`, 115, 63);
      doc.text(`Zona Waktu: ${timezoneSettings.timezone}`, 155, 63);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Hadir Tepat: ${totalTepat}`, 30, 73);
      doc.text(`Terlambat: ${totalTerlambat}`, 70, 73);
      doc.text(`Mangkir: ${totalMangkir}`, 115, 73);
      doc.text(`Izin: ${totalIzin}`, 155, 73);
      
      // === PERFORMANCE CHART SECTION ===
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('GRAFIK KINERJA', 25, 88);
      
      // Draw bar chart for top 10 teachers - using exportData
      const chartStartX = 25;
      const chartStartY = 92;
      const chartWidth = pageWidth - 50;
      const chartHeight = 35;
      const maxBars = Math.min(10, exportData.length || 0);
      
      if (maxBars > 0) {
        const barWidth = chartWidth / maxBars - 4;
        const sortedData = [...exportData].sort((a, b) => b.skor_kinerja - a.skor_kinerja).slice(0, maxBars);
        
        // Draw bars
        sortedData.forEach((g, idx) => {
          const barHeight = (g.skor_kinerja / 100) * chartHeight;
          const x = chartStartX + idx * (barWidth + 4);
          const y = chartStartY + chartHeight - barHeight;
          
          // Bar color based on score
          if (g.skor_kinerja >= 75) {
            doc.setFillColor(16, 185, 129); // green
          } else if (g.skor_kinerja >= 50) {
            doc.setFillColor(245, 158, 11); // yellow
          } else {
            doc.setFillColor(239, 68, 68); // red
          }
          
          doc.roundedRect(x, y, barWidth, barHeight, 1, 1, 'F');
          
          // Score label on top
          doc.setFontSize(6);
          doc.setTextColor(30, 27, 75);
          doc.text(`${g.skor_kinerja.toFixed(0)}`, x + barWidth/2, y - 2, { align: 'center' });
          
          // Name label at bottom (truncated)
          doc.setFontSize(5);
          const shortName = g.guru_nama.split(' ')[0].substring(0, 8);
          doc.text(shortName, x + barWidth/2, chartStartY + chartHeight + 5, { align: 'center' });
        });
        
        doc.setTextColor(0, 0, 0);
      }
      
      // Pie chart for attendance distribution
      const pieX = pageWidth - 45;
      const pieY = chartStartY + 15;
      const pieRadius = 12;
      
      const totalAll = totalTepat + totalTerlambat + totalMangkir + totalIzin;
      if (totalAll > 0) {
        const pieData = [
          { value: totalTepat, color: [16, 185, 129], label: 'Tepat' },
          { value: totalTerlambat, color: [245, 158, 11], label: 'Terlambat' },
          { value: totalMangkir, color: [239, 68, 68], label: 'Mangkir' },
          { value: totalIzin, color: [59, 130, 246], label: 'Izin' }
        ];
        
        let startAngle = 0;
        pieData.forEach(segment => {
          if (segment.value > 0) {
            const sliceAngle = (segment.value / totalAll) * 2 * Math.PI;
            
            doc.setFillColor(...segment.color);
            
            // Draw pie slice using multiple small triangles
            for (let a = startAngle; a < startAngle + sliceAngle; a += 0.1) {
              const x1 = pieX + Math.cos(a) * pieRadius;
              const y1 = pieY + Math.sin(a) * pieRadius;
              const x2 = pieX + Math.cos(a + 0.1) * pieRadius;
              const y2 = pieY + Math.sin(a + 0.1) * pieRadius;
              doc.triangle(pieX, pieY, x1, y1, x2, y2, 'F');
            }
            
            startAngle += sliceAngle;
          }
        });
        
        // Legend for pie chart
        doc.setFontSize(5);
        doc.setFillColor(16, 185, 129);
        doc.rect(pieX - 20, pieY + pieRadius + 5, 3, 3, 'F');
        doc.text('Tepat', pieX - 16, pieY + pieRadius + 8);
        
        doc.setFillColor(245, 158, 11);
        doc.rect(pieX - 5, pieY + pieRadius + 5, 3, 3, 'F');
        doc.text('Terlambat', pieX - 1, pieY + pieRadius + 8);
        
        doc.setFillColor(239, 68, 68);
        doc.rect(pieX - 20, pieY + pieRadius + 12, 3, 3, 'F');
        doc.text('Mangkir', pieX - 16, pieY + pieRadius + 15);
        
        doc.setFillColor(59, 130, 246);
        doc.rect(pieX - 5, pieY + pieRadius + 12, 3, 3, 'F');
        doc.text('Izin', pieX - 1, pieY + pieRadius + 15);
      }
      
      // Table Data with additional columns - using exportData (ALL data)
      const tableData = exportData.map((g, idx) => [
        idx + 1,
        g.guru_nama,
        g.guru_nip || '-',
        g.jabatan || 'Guru',
        g.status_kepegawaian || '-',
        g.hadir_tepat_waktu,
        g.hadir_terlambat,
        g.mangkir,
        g.izin,
        `${g.skor_kinerja.toFixed(1)}`
      ]) || [];

      autoTable(doc, {
        head: [['No', 'Nama', 'NIP/NIPPPK/NUPTK/NIK', 'Jabatan', 'Status Kepeg.', 'Tepat', 'Terlambat', 'Mangkir', 'Izin', 'Skor']],
        body: tableData,
        startY: 140,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [30, 27, 75], textColor: 255, fontStyle: 'bold', fontSize: 6 },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 28 },
          2: { cellWidth: 25 },
          3: { cellWidth: 22 },
          4: { cellWidth: 18 },
          5: { halign: 'center', cellWidth: 12 },
          6: { halign: 'center', cellWidth: 14 },
          7: { halign: 'center', cellWidth: 12 },
          8: { halign: 'center', cellWidth: 10 },
          9: { halign: 'center', fontStyle: 'bold', cellWidth: 12 }
        }
      });
      
      // Footer
      const finalY = doc.lastAutoTable?.finalY || 200;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak pada: ${new Date().toLocaleDateString('id-ID', { 
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })} ${timezoneSettings.timezone}`, 20, finalY + 15);
      doc.text('Dokumen ini digenerate oleh sistem SIGAP GURU', 20, finalY + 20);
      
      // Signature area
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Mengetahui,', pageWidth - 60, finalY + 30);
      doc.text('Kepala Dinas Pendidikan', pageWidth - 70, finalY + 35);
      doc.text('Kabupaten Asmat', pageWidth - 60, finalY + 40);
      doc.line(pageWidth - 80, finalY + 55, pageWidth - 30, finalY + 55);
      doc.text('NIP. ......................', pageWidth - 70, finalY + 60);

      doc.save(`laporan-kinerja-${getMonthName(selectedMonth)}-${selectedYear}.pdf`);
      toast.success('Laporan PDF berhasil diunduh', {
        description: `${pdfTotalGuru} data pegawai diexport`
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Gagal mengunduh PDF');
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel - Enhanced version
  const exportExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL data without pagination for export
      const sekolahId = selectedSekolah === 'all' ? null : selectedSekolah;
      const allData = await getLaporanKinerja(selectedMonth, selectedYear, sekolahId, null, 0, null);
      const exportData = allData?.data || [];
      
      if (exportData.length === 0) {
        toast.error('Tidak ada data untuk diexport');
        return;
      }
      
      // Prepare data for main sheet with additional columns
      const data = exportData.map((g, idx) => ({
        'No': idx + 1,
        'Nama': g.guru_nama,
        'NIP/NIPPPK/NUPTK/NIK': g.guru_nip || '-',
        'Jabatan': g.jabatan || 'Guru',
        'Status Kepegawaian': g.status_kepegawaian || '-',
        'Sekolah': g.sekolah_nama,
        'Hadir Tepat Waktu': g.hadir_tepat_waktu,
        'Hadir Terlambat': g.hadir_terlambat,
        'Mangkir': g.mangkir,
        'Izin': g.izin,
        'Skor Kinerja': g.skor_kinerja
      })) || [];

      // Calculate summary - using exportData (all data)
      const avgScore = exportData.length > 0 
        ? (exportData.reduce((sum, g) => sum + g.skor_kinerja, 0) / exportData.length).toFixed(1)
        : 0;
      const totalTepat = exportData.reduce((sum, g) => sum + g.hadir_tepat_waktu, 0) || 0;
      const totalTerlambat = exportData.reduce((sum, g) => sum + g.hadir_terlambat, 0) || 0;
      const totalMangkir = exportData.reduce((sum, g) => sum + g.mangkir, 0) || 0;
      const totalIzin = exportData.reduce((sum, g) => sum + g.izin, 0) || 0;

      // Summary sheet data
      const summaryData = [
        { 'Keterangan': 'LAPORAN KINERJA PEGAWAI', 'Nilai': '' },
        { 'Keterangan': 'Dinas Pendidikan Kabupaten Asmat', 'Nilai': '' },
        { 'Keterangan': '', 'Nilai': '' },
        { 'Keterangan': 'Periode', 'Nilai': `${getMonthName(selectedMonth)} ${selectedYear}` },
        { 'Keterangan': 'Sekolah', 'Nilai': getSelectedSekolahName() },
        { 'Keterangan': 'Zona Waktu', 'Nilai': timezoneSettings.timezone },
        { 'Keterangan': '', 'Nilai': '' },
        { 'Keterangan': 'Total Pegawai', 'Nilai': exportData.length || 0 },
        { 'Keterangan': 'Total Hari Kerja', 'Nilai': allData?.total_hari_kerja || 0 },
        { 'Keterangan': 'Rata-rata Skor', 'Nilai': avgScore },
        { 'Keterangan': '', 'Nilai': '' },
        { 'Keterangan': 'Total Hadir Tepat Waktu', 'Nilai': totalTepat },
        { 'Keterangan': 'Total Hadir Terlambat', 'Nilai': totalTerlambat },
        { 'Keterangan': 'Total Mangkir', 'Nilai': totalMangkir },
        { 'Keterangan': 'Total Izin', 'Nilai': totalIzin },
        { 'Keterangan': '', 'Nilai': '' },
        { 'Keterangan': 'Dicetak pada', 'Nilai': new Date().toLocaleString('id-ID') }
      ];

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      
      // Summary sheet
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Ringkasan');
      
      // Data sheet with expanded columns
      const wsData = XLSX.utils.json_to_sheet(data);
      wsData['!cols'] = [
        { wch: 5 },   // No
        { wch: 25 },  // Nama
        { wch: 25 },  // NIP/NIPPPK/NUPTK/NIK
        { wch: 18 },  // Jabatan
        { wch: 18 },  // Status Kepegawaian
        { wch: 25 },  // Sekolah
        { wch: 15 },  // Hadir Tepat Waktu
        { wch: 12 },  // Hadir Terlambat
        { wch: 10 },  // Mangkir
        { wch: 8 },   // Izin
        { wch: 12 }   // Skor Kinerja
      ];
      XLSX.utils.book_append_sheet(wb, wsData, 'Detail Kinerja');

      // Per-school summary if showing all schools - using exportData
      if (selectedSekolah === 'all' && exportData.length > 0) {
        const schoolSummary = {};
        exportData.forEach(g => {
          if (!schoolSummary[g.sekolah_nama]) {
            schoolSummary[g.sekolah_nama] = {
              jumlah_guru: 0,
              total_skor: 0,
              tepat_waktu: 0,
              terlambat: 0,
              mangkir: 0,
              izin: 0
            };
          }
          schoolSummary[g.sekolah_nama].jumlah_guru++;
          schoolSummary[g.sekolah_nama].total_skor += g.skor_kinerja;
          schoolSummary[g.sekolah_nama].tepat_waktu += g.hadir_tepat_waktu;
          schoolSummary[g.sekolah_nama].terlambat += g.hadir_terlambat;
          schoolSummary[g.sekolah_nama].mangkir += g.mangkir;
          schoolSummary[g.sekolah_nama].izin += g.izin;
        });

        const schoolData = Object.entries(schoolSummary).map(([nama, data], idx) => ({
          'No': idx + 1,
          'Nama Sekolah': nama,
          'Jumlah Guru': data.jumlah_guru,
          'Rata-rata Skor': (data.total_skor / data.jumlah_guru).toFixed(1),
          'Hadir Tepat': data.tepat_waktu,
          'Terlambat': data.terlambat,
          'Mangkir': data.mangkir,
          'Izin': data.izin
        }));

        const wsSchool = XLSX.utils.json_to_sheet(schoolData);
        wsSchool['!cols'] = [
          { wch: 5 }, { wch: 30 }, { wch: 12 }, { wch: 12 },
          { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 8 }
        ];
        XLSX.utils.book_append_sheet(wb, wsSchool, 'Rekap Sekolah');
      }

      XLSX.writeFile(wb, `laporan-kinerja-${getMonthName(selectedMonth)}-${selectedYear}.xlsx`);
      toast.success('Laporan Excel berhasil diunduh', {
        description: `${data.length} data guru dalam ${selectedSekolah === 'all' ? '3' : '2'} sheet`
      });
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Gagal mengunduh Excel');
    } finally {
      setExporting(false);
    }
  };

  // Export Individual Report PDF
  const exportIndividualPDF = async (guruId, guruNama) => {
    setExportingIndividual(guruId);
    try {
      // Fetch individual report data
      const data = await getLaporanIndividu(guruId, selectedMonth, selectedYear);
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('PEMERINTAH KABUPATEN ASMAT', pageWidth / 2, 15, { align: 'center' });
      doc.setFontSize(12);
      doc.text('DINAS PENDIDIKAN', pageWidth / 2, 22, { align: 'center' });
      
      // Line separator
      doc.setDrawColor(30, 27, 75);
      doc.setLineWidth(0.5);
      doc.line(20, 27, pageWidth - 20, 27);
      doc.setLineWidth(0.2);
      doc.line(20, 28, pageWidth - 20, 28);
      
      // Report Title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('LAPORAN KINERJA INDIVIDU', pageWidth / 2, 38, { align: 'center' });
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Periode: ${getMonthName(selectedMonth)} ${selectedYear}`, pageWidth / 2, 45, { align: 'center' });
      
      // Biodata Section
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(20, 52, pageWidth - 40, 40, 3, 3, 'F');
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('DATA GURU', 25, 60);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const biodata = data.biodata;
      doc.text(`Nama Lengkap: ${biodata.nama}`, 25, 68);
      doc.text(`NIP: ${biodata.nip || '-'}`, 25, 75);
      doc.text(`NUPTK: ${biodata.nuptk || '-'}`, 25, 82);
      doc.text(`Sekolah: ${biodata.sekolah_nama}`, 110, 68);
      doc.text(`Jabatan: ${biodata.jabatan}`, 110, 75);
      doc.text(`Email: ${biodata.email || '-'}`, 110, 82);
      
      // Summary Statistics
      const ringkasan = data.ringkasan;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('RINGKASAN KEHADIRAN', 25, 100);
      
      // Stats boxes
      const boxY = 105;
      const boxWidth = 40;
      const boxGap = 5;
      
      // Hadir Tepat
      doc.setFillColor(16, 185, 129);
      doc.roundedRect(20, boxY, boxWidth, 25, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(ringkasan.hadir_tepat_waktu), 40, boxY + 12, { align: 'center' });
      doc.setFontSize(8);
      doc.text('Tepat Waktu', 40, boxY + 20, { align: 'center' });
      
      // Terlambat
      doc.setFillColor(245, 158, 11);
      doc.roundedRect(20 + boxWidth + boxGap, boxY, boxWidth, 25, 2, 2, 'F');
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(ringkasan.hadir_terlambat), 40 + boxWidth + boxGap, boxY + 12, { align: 'center' });
      doc.setFontSize(8);
      doc.text('Terlambat', 40 + boxWidth + boxGap, boxY + 20, { align: 'center' });
      
      // Mangkir
      doc.setFillColor(239, 68, 68);
      doc.roundedRect(20 + (boxWidth + boxGap) * 2, boxY, boxWidth, 25, 2, 2, 'F');
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(ringkasan.mangkir), 40 + (boxWidth + boxGap) * 2, boxY + 12, { align: 'center' });
      doc.setFontSize(8);
      doc.text('Mangkir', 40 + (boxWidth + boxGap) * 2, boxY + 20, { align: 'center' });
      
      // Izin
      doc.setFillColor(59, 130, 246);
      doc.roundedRect(20 + (boxWidth + boxGap) * 3, boxY, boxWidth, 25, 2, 2, 'F');
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(ringkasan.izin), 40 + (boxWidth + boxGap) * 3, boxY + 12, { align: 'center' });
      doc.setFontSize(8);
      doc.text('Izin', 40 + (boxWidth + boxGap) * 3, boxY + 20, { align: 'center' });
      
      doc.setTextColor(0, 0, 0);
      
      // Performance Score Box
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('PENILAIAN KINERJA', 25, 145);
      
      // Score circle
      const penilaian = data.penilaian;
      const scoreColor = penilaian.color;
      const rgb = hexToRgb(scoreColor);
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.circle(50, 165, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(penilaian.grade, 50, 170, { align: 'center' });
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.text(`Skor: ${data.skor_kinerja}`, 75, 160);
      doc.setFontSize(10);
      doc.text(`Predikat: ${penilaian.label}`, 75, 168);
      doc.text(`Kehadiran: ${ringkasan.persentase_kehadiran}%`, 75, 176);
      
      // Daily Chart as simple bar representation
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('GRAFIK KEHADIRAN HARIAN', 120, 145);
      
      const chartStartX = 120;
      const chartStartY = 150;
      const barWidth = 3;
      const maxHeight = 30;
      
      data.daily_chart.slice(0, 22).forEach((day, idx) => {
        const barHeight = (day.nilai / 100) * maxHeight;
        let color;
        switch(day.status) {
          case 'hadir_tepat_waktu': color = [16, 185, 129]; break;
          case 'hadir_terlambat': color = [245, 158, 11]; break;
          case 'izin': color = [59, 130, 246]; break;
          default: color = [239, 68, 68];
        }
        doc.setFillColor(...color);
        doc.rect(chartStartX + (idx * (barWidth + 1)), chartStartY + (maxHeight - barHeight), barWidth, barHeight, 'F');
      });
      
      // Attendance Detail Table
      const tableData = data.detail_absensi.map((a, idx) => [
        idx + 1,
        a.tanggal,
        a.hari,
        a.waktu,
        a.status === 'hadir_tepat_waktu' ? 'Tepat Waktu' : (a.status === 'hadir_terlambat' ? 'Terlambat' : a.status),
        a.dalam_radius ? 'Ya' : 'Tidak',
        a.face_verified ? 'Ya' : 'Tidak'
      ]);
      
      autoTable(doc, {
        head: [['No', 'Tanggal', 'Hari', 'Waktu', 'Status', 'Dalam Radius', 'Face Verified']],
        body: tableData,
        startY: 190,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [30, 27, 75], textColor: 255 },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          5: { halign: 'center' },
          6: { halign: 'center' }
        }
      });
      
      // Footer
      const finalY = doc.lastAutoTable?.finalY || 250;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak pada: ${new Date().toLocaleDateString('id-ID', { 
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })} ${data.periode.timezone}`, 20, finalY + 10);
      
      // Signature
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Mengetahui,', pageWidth - 60, finalY + 20);
      doc.text('Kepala Sekolah', pageWidth - 55, finalY + 25);
      doc.line(pageWidth - 75, finalY + 40, pageWidth - 25, finalY + 40);
      
      doc.save(`laporan-individu-${biodata.nama.replace(/\s/g, '-')}-${getMonthName(selectedMonth)}-${selectedYear}.pdf`);
      toast.success('Laporan individu berhasil diunduh', {
        description: biodata.nama
      });
    } catch (error) {
      console.error('Individual export error:', error);
      toast.error('Gagal mengunduh laporan individu');
    } finally {
      setExportingIndividual(null);
    }
  };

  // Helper function to convert hex to rgb
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  // Calculate averages from aggregate data
  const avgScore = kinerjaAggregate?.skor_rata_rata?.toFixed(1) || 0;
  const totalGuru = kinerjaAggregate?.total_guru || 0;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="laporan-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950">Laporan Kinerja</h1>
            <p className="text-gray-500">Rekap kehadiran dan skor kinerja guru</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdminDinas && (
            <Select value={selectedSekolah} onValueChange={(v) => { setSelectedSekolah(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[180px]" data-testid="sekolah-select">
                <SelectValue placeholder="Semua Sekolah" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Sekolah</SelectItem>
                {sekolahList.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={String(selectedMonth)} onValueChange={(v) => { setSelectedMonth(Number(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-[120px]" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>{getMonthName(i + 1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => { setSelectedYear(Number(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-[90px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-indigo-100/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-950">{totalGuru}</p>
                    <p className="text-xs text-gray-500">Total Guru</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-indigo-100/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${getScoreColor(Number(avgScore))}`}>{avgScore}</p>
                    <p className="text-xs text-gray-500">Rata-rata Skor</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-indigo-100/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Award className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-950">{laporan?.total_hari_kerja || 0}</p>
                    <p className="text-xs text-gray-500">Hari Kerja</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-indigo-100/50">
              <CardContent className="p-4 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportPDF}
                  disabled={exporting || !laporan?.data?.length}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="export-pdf-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-1" />
                  )}
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportExcel}
                  disabled={exporting || !laporan?.data?.length}
                  className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                  data-testid="export-excel-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                  )}
                  Excel
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Top Performers Bar Chart */}
            <Card className="border-indigo-100/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Top 5 Guru Terbaik
                </CardTitle>
                <CardDescription>
                  Berdasarkan skor kinerja tertinggi
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value) => [`${value.toFixed(1)}`, 'Skor']} />
                        <Bar dataKey="skor" fill="#4f46e5" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      Tidak ada data untuk periode ini
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Grade Distribution Pie Chart */}
            <Card className="border-indigo-100/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Distribusi Kinerja Guru
                </CardTitle>
                <CardDescription>
                  {totalGuru} guru • Rata-rata: {avgScore}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {pieDataGrades.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieDataGrades}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieDataGrades.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      Tidak ada data kinerja
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Data Table with Search & Pagination */}
          <Card className="border-indigo-100/50">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Detail Kinerja Guru
                  </CardTitle>
                  <CardDescription>
                    {getMonthName(selectedMonth)} {selectedYear} • Menampilkan {startIndex}-{endIndex} dari {totalItems} guru
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Cari nama guru..."
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-9 w-[200px]"
                      data-testid="search-guru-input"
                    />
                  </div>
                  <Button variant="outline" size="icon" onClick={loadData} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Peringkat</th>
                      <th>Nama</th>
                      <th>NIP</th>
                      <th>Sekolah</th>
                      <th className="text-center">Tepat Waktu</th>
                      <th className="text-center">Terlambat</th>
                      <th className="text-center">Mangkir</th>
                      <th className="text-center">Izin</th>
                      <th className="text-center">Skor</th>
                      {isAdminDinas && <th className="text-center">Aksi</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {laporan?.data?.map((guru) => {
                      const scoreBadge = getScoreBadge(guru.skor_kinerja);
                      return (
                        <tr key={guru.guru_id}>
                          <td>
                            <div className="flex items-center gap-2">
                              {guru.ranking <= 3 ? (
                                <Badge className={
                                  guru.ranking === 1 ? 'bg-amber-400 text-amber-900' :
                                  guru.ranking === 2 ? 'bg-gray-300 text-gray-700' :
                                  'bg-amber-600 text-white'
                                }>
                                  #{guru.ranking}
                                </Badge>
                              ) : (
                                <span className="text-gray-500 pl-2">{guru.ranking}</span>
                              )}
                            </div>
                          </td>
                          <td className="font-medium">{guru.guru_nama}</td>
                          <td className="font-mono text-sm">{guru.guru_nip}</td>
                          <td>{guru.sekolah_nama}</td>
                          <td className="text-center text-emerald-600 font-medium">{guru.hadir_tepat_waktu}</td>
                          <td className="text-center text-amber-600 font-medium">{guru.hadir_terlambat}</td>
                          <td className="text-center text-red-600 font-medium">{guru.mangkir}</td>
                          <td className="text-center text-blue-600 font-medium">{guru.izin}</td>
                          <td className="text-center">
                            <Badge className={scoreBadge.color}>
                              {guru.skor_kinerja.toFixed(1)}
                            </Badge>
                          </td>
                          {isAdminDinas && (
                            <td className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => exportIndividualPDF(guru.guru_id, guru.guru_nama)}
                                disabled={exportingIndividual === guru.guru_id}
                                className="text-indigo-600 hover:bg-indigo-50"
                                data-testid={`export-individual-${guru.guru_id}`}
                              >
                                {exportingIndividual === guru.guru_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <UserCircle className="w-4 h-4" />
                                )}
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(!laporan?.data || laporan.data.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    {debouncedSearch ? `Tidak ada guru dengan nama "${debouncedSearch}"` : 'Tidak ada data untuk periode ini'}
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    Halaman {currentPage} dari {totalPages}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      data-testid="pagination-first"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    {getPageNumbers().map(page => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? "bg-indigo-600" : ""}
                        data-testid={`pagination-page-${page}`}
                      >
                        {page}
                      </Button>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      data-testid="pagination-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      data-testid="pagination-last"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default LaporanPage;
